/**
 * Unified Real-time Service - Phase 4 Architecture Migration
 *
 * The definitive real-time synchronization service for the entire application
 * Consolidates all fragmented real-time implementations into a single, unified service
 * Provides type-safe subscriptions, performance monitoring, and intelligent deduplication
 */

import type { TableRow } from "@/lib/typed-supabase";
import { typedSupabase } from "@/lib/typed-supabase";
import {
  IService,
  ServiceResponse,
} from "@/services/core/service-registry";
import type { AdminBooking } from "@/types/admin-calendar";
import type { Notification } from "@/types/notification";
import type {
  ChannelStatus,
  RealtimeCallback,
  RealtimeFilter,
  RealtimeMetrics,
  RealtimePayload,
  RealtimeTableMap,
  SubscriptionConfig,
  UnifiedRealtimeConfig,
  UnsubscribeFunction,
} from "@/types/realtime";
import type { Todo } from "@/types/todo";
import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
} from "@supabase/supabase-js";
import { logger } from "@/lib/logger";

// ====================================================================================================
// 🎯 UNIFIED REAL-TIME SERVICE - Using centralized types from @/types/realtime
// ====================================================================================================

// ====================================================================================================
// 🔄 MESSAGE DEDUPLICATION - Prevent duplicate processing across multiple subscribers
// ====================================================================================================

interface DedupMessage {
  id: string;
  timestamp: number;
  processed: boolean;
}

class MessageDeduplicator {
  private messages = new Map<string, DedupMessage>();
  private readonly DEFAULT_WINDOW = 1000; // 1 second

  /**
   * Check if message should be processed (not a duplicate)
   */
  shouldProcess(
    table: string,
    eventType: string,
    recordId: string,
    timestamp: number,
    windowMs?: number
  ): boolean {
    const messageId = `${table}:${eventType}:${recordId}`;
    const window = windowMs ?? this.DEFAULT_WINDOW;

    // Clean old messages
    this.cleanup(timestamp - window);

    const existing = this.messages.get(messageId);
    if (existing && timestamp - existing.timestamp < window) {
      return false; // Duplicate within window
    }

    // Mark as processed
    this.messages.set(messageId, {
      id: messageId,
      timestamp,
      processed: true,
    });

    return true;
  }

  /**
   * Clean up old deduplication entries
   */
  private cleanup(cutoffTimestamp: number): void {
    for (const [id, message] of this.messages.entries()) {
      if (message.timestamp < cutoffTimestamp) {
        this.messages.delete(id);
      }
    }
  }

  /**
   * Get deduplication stats
   */
  getStats(): { totalMessages: number; duplicatesBlocked: number } {
    return {
      totalMessages: this.messages.size,
      duplicatesBlocked: Array.from(this.messages.values()).filter(
        (m) => !m.processed
      ).length,
    };
  }
}

// ====================================================================================================
// 🚀 SERVICE INTERFACE DEFINITION
// ====================================================================================================

export interface IUnifiedRealtimeService extends IService {
  // Type-safe subscription methods
  subscribeToTable<TTable extends keyof RealtimeTableMap>(
    table: TTable,
    callback: RealtimeCallback<RealtimeTableMap[TTable]>,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  // Convenience methods for common tables
  subscribeToTodos(
    callback: RealtimeCallback<Todo>,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  subscribeToNotifications(
    callback: RealtimeCallback<Notification>,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  subscribeToBookings(
    callback: RealtimeCallback<AdminBooking>,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  subscribeToTaskComments(
    callback: RealtimeCallback<TableRow<"task_comments">>,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  subscribeToTaskAttachments(
    callback: RealtimeCallback<TableRow<"task_attachments">>,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  subscribeToAuditLogs(
    callback: RealtimeCallback<TableRow<"audit_logs">>,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  // 🚀 TIME MANAGEMENT REAL-TIME METHODS
  subscribeToTimeBlocks(
    callback: RealtimeCallback<import("@/types/time-management").TimeBlock>,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  subscribeToTaskDependencies(
    callback: RealtimeCallback<
      import("@/types/time-management").TaskDependency
    >,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  subscribeToTaskEstimations(
    callback: RealtimeCallback<
      import("@/types/time-management").TaskEstimation
    >,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  subscribeToRecurrencePatterns(
    callback: RealtimeCallback<
      import("@/types/time-management").RecurrencePattern
    >,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  subscribeToWorkloadTracking(
    callback: RealtimeCallback<
      import("@/types/time-management").WorkloadTracking
    >,
    config?: SubscriptionConfig
  ): Promise<ServiceResponse<UnsubscribeFunction>>;

  // Broadcast functionality
  broadcast(
    channel: string,
    event: string,
    payload: unknown
  ): Promise<ServiceResponse<void>>;

  // Monitoring and management
  getMetrics(): ServiceResponse<RealtimeMetrics>;
  getChannelStatus(): ServiceResponse<ChannelStatus[]>;
  getConnectionHealth(): ServiceResponse<{
    healthy: boolean;
    issues: string[];
  }>;
  clearMetrics(): ServiceResponse<void>;
}

// ====================================================================================================
// 🚀 UNIFIED REAL-TIME SERVICE IMPLEMENTATION
// ====================================================================================================

export class UnifiedRealtimeService implements IUnifiedRealtimeService {
  readonly name = "unified-realtime" as const;
  private readonly supabase = typedSupabase;
  private readonly config: UnifiedRealtimeConfig;

  // Core state management
  private channels = new Map<string, RealtimeChannel>();
  private subscribers = new Map<string, Set<RealtimeCallback>>();
  private reconnectAttempts = new Map<string, number>();

  // Performance monitoring
  private metrics: RealtimeMetrics;
  private deduplicator: MessageDeduplicator;
  private startTime: number;

  constructor(config: Partial<UnifiedRealtimeConfig> = {}) {
    this.config = {
      enableSync: true,
      reconnectInterval: 5000,
      maxReconnectAttempts: 5,
      enableMetrics: true,
      enableDeduplication: true,
      globalDebounceMs: 100,
      enablePerformanceMonitoring: true,
      maxChannels: 50,
      ...config,
    };

    this.startTime = Date.now();
    this.deduplicator = new MessageDeduplicator();
    this.metrics = this.initializeMetrics();
  }

  /**
   * Initialize the unified real-time service
   */
  async initialize(): Promise<void> {
    if (!this.config.enableSync) {
      logger.info("🔄 Unified Real-time Service initialized (sync disabled)");
      return;
    }

    logger.info("🚀 Unified Real-time Service initialized");
    logger.info("📊 Features enabled:", {
      sync: this.config.enableSync,
      metrics: this.config.enableMetrics,
      deduplication: this.config.enableDeduplication,
      performanceMonitoring: this.config.enablePerformanceMonitoring,
    });

    if (this.config.enablePerformanceMonitoring) {
      this.startPerformanceMonitoring();
    }
  }

  /**
   * Dispose of the unified real-time service
   */
  async dispose(): Promise<void> {
    logger.info("🔄 Disposing Unified Real-time Service...");

    // Close all channels
    for (const [channelName, channel] of this.channels.entries()) {
      try {
        await this.supabase.removeChannel(channel);
        logger.info(`📡 Closed channel: ${channelName}`);
      } catch (error) {
        logger.warn(`⚠️ Error closing channel ${channelName}:`, { error });
      }
    }

    // Clear all state
    this.channels.clear();
    this.subscribers.clear();
    this.reconnectAttempts.clear();

    logger.info("✅ Unified Real-time Service disposed");
  }

  /**
   * Subscribe to real-time updates for any supported table with full type safety
   */
  async subscribeToTable<TTable extends keyof RealtimeTableMap>(
    table: TTable,
    callback: RealtimeCallback<RealtimeTableMap[TTable]>,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    if (!this.config.enableSync) {
      return { success: false, error: "Real-time sync is disabled" };
    }

    try {
      const channelKey = this.generateChannelKey(table, config.filter);

      // Initialize subscribers set for this channel
      if (!this.subscribers.has(channelKey)) {
        this.subscribers.set(channelKey, new Set());
      }

      // Add callback to subscribers
      this.subscribers.get(channelKey)!.add(callback as RealtimeCallback);

      // Create channel if it doesn't exist
      if (!this.channels.has(channelKey)) {
        await this.createChannel(table, config, channelKey);
      }

      this.updateMetrics({ subscriptionsTotal: this.getTotalSubscriptions() });

      // Return unsubscribe function
      const unsubscribe: UnsubscribeFunction = () => {
        this.unsubscribeCallback(channelKey, callback as RealtimeCallback);
      };

      return { success: true, data: unsubscribe };
    } catch (error) {
      logger.error(`❌ Failed to subscribe to ${table}:`, { error });
      const serviceError =
        error instanceof Error
          ? error
          : new Error("Unknown subscription error");

      return { success: false, error: serviceError.message };
    }
  }

  /**
   * Subscribe to Todo changes with enhanced filtering
   */
  async subscribeToTodos(
    callback: RealtimeCallback<Todo>,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("todos", callback, config);
  }

  /**
   * Subscribe to Notification changes
   */
  async subscribeToNotifications(
    callback: RealtimeCallback<Notification>,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("notifications", callback, config);
  }

  /**
   * Subscribe to Admin Booking changes
   */
  async subscribeToBookings(
    callback: RealtimeCallback<AdminBooking>,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("google_bookings", callback, config);
  }

  /**
   * Subscribe to Task Comment changes
   */
  async subscribeToTaskComments(
    callback: RealtimeCallback<TableRow<"task_comments">>,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("task_comments", callback, config);
  }

  /**
   * Subscribe to Task Attachment changes
   */
  async subscribeToTaskAttachments(
    callback: RealtimeCallback<TableRow<"task_attachments">>,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("task_attachments", callback, config);
  }

  /**
   * Subscribe to Audit Log changes
   */
  async subscribeToAuditLogs(
    callback: RealtimeCallback<TableRow<"audit_logs">>,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("audit_logs", callback, config);
  }

  // 🚀 TIME MANAGEMENT REAL-TIME SUBSCRIPTION METHODS

  /**
   * Subscribe to Time Block changes - Real-time time blocking
   */
  async subscribeToTimeBlocks(
    callback: RealtimeCallback<import("@/types/time-management").TimeBlock>,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("time_blocks", callback, config);
  }

  /**
   * Subscribe to Task Dependency changes - Real-time dependency tracking
   */
  async subscribeToTaskDependencies(
    callback: RealtimeCallback<
      import("@/types/time-management").TaskDependency
    >,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("task_dependencies", callback, config);
  }

  /**
   * Subscribe to Task Estimation changes - Real-time estimation updates
   */
  async subscribeToTaskEstimations(
    callback: RealtimeCallback<
      import("@/types/time-management").TaskEstimation
    >,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("task_estimations", callback, config);
  }

  /**
   * Subscribe to Recurrence Pattern changes - Real-time recurring task updates
   */
  async subscribeToRecurrencePatterns(
    callback: RealtimeCallback<
      import("@/types/time-management").RecurrencePattern
    >,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("recurrence_patterns", callback, config);
  }

  /**
   * Subscribe to Workload Tracking changes - Real-time workload analytics
   */
  async subscribeToWorkloadTracking(
    callback: RealtimeCallback<
      import("@/types/time-management").WorkloadTracking
    >,
    config: SubscriptionConfig = {}
  ): Promise<ServiceResponse<UnsubscribeFunction>> {
    return this.subscribeToTable("workload_tracking", callback, config);
  }

  /**
   * Broadcast a custom event to all subscribers of a channel
   */
  async broadcast(
    channel: string,
    event: string,
    payload: unknown
  ): Promise<ServiceResponse<void>> {
    try {
      const channelInstance = this.channels.get(channel);
      if (!channelInstance) {
        return { success: false, error: `Channel ${channel} not found` };
      }

      await channelInstance.send({
        type: "broadcast",
        event,
        payload,
      });

      return { success: true, data: undefined };
    } catch (error) {
      logger.error(`❌ Failed to broadcast to ${channel}:`, { error });
      const serviceError =
        error instanceof Error ? error : new Error("Unknown broadcast error");
      return { success: false, error: serviceError.message };
    }
  }

  /**
   * Get comprehensive performance metrics
   */
  getMetrics(): ServiceResponse<RealtimeMetrics> {
    const currentMetrics = {
      ...this.metrics,
      connectionsActive: this.channels.size,
      subscriptionsTotal: this.getTotalSubscriptions(),
      uptime: Date.now() - this.startTime,
    };

    return { success: true, data: currentMetrics };
  }

  /**
   * Get status of all active channels
   */
  getChannelStatus(): ServiceResponse<ChannelStatus[]> {
    const statusList: ChannelStatus[] = [];

    for (const [name, channel] of this.channels.entries()) {
      const subscriberCount = this.subscribers.get(name)?.size ?? 0;
      statusList.push({
        name,
        state:
          (channel?.state as "closed" | "joining" | "joined" | "leaving") ||
          "closed",
        subscriberCount,
        lastActivity: new Date(), // TODO: Track actual last activity
        errorCount: this.reconnectAttempts.get(name) ?? 0,
      });
    }

    return { success: true, data: statusList };
  }

  /**
   * Get overall connection health status
   */
  getConnectionHealth(): ServiceResponse<{
    healthy: boolean;
    issues: string[];
  }> {
    const issues: string[] = [];
    let healthy = true;

    // Check if we have too many failed connections
    const totalReconnects = Array.from(this.reconnectAttempts.values()).reduce(
      (sum, count) => sum + count,
      0
    );
    if (totalReconnects > this.config.maxReconnectAttempts * 3) {
      issues.push(`High reconnection attempts: ${totalReconnects}`);
      healthy = false;
    }

    // Check if error rate is too high
    if (this.metrics.errorRate > 0.1) {
      // 10% error rate threshold
      issues.push(
        `High error rate: ${(this.metrics.errorRate * 100).toFixed(1)}%`
      );
      healthy = false;
    }

    // Check if we're hitting channel limits
    if (this.channels.size >= this.config.maxChannels * 0.9) {
      issues.push(
        `Approaching channel limit: ${this.channels.size}/${this.config.maxChannels}`
      );
    }

    return { success: true, data: { healthy, issues } };
  }

  /**
   * Clear performance metrics (useful for testing)
   */
  clearMetrics(): ServiceResponse<void> {
    this.metrics = this.initializeMetrics();
    this.reconnectAttempts.clear();
    return { success: true, data: undefined };
  }

  // ====================================================================================================
  // 🔧 PRIVATE IMPLEMENTATION METHODS
  // ====================================================================================================

  /**
   * Create a real-time channel for a table with advanced configuration
   */
  private async createChannel(
    table: keyof RealtimeTableMap,
    config: SubscriptionConfig,
    channelKey: string
  ): Promise<void> {
    try {
      const events = config.events ?? ["*"];
      const pgConfig: {
        event: string;
        schema: string;
        table: string;
        filter?: string;
      } = {
        event: events.length === 1 ? events[0] : "*",
        schema: "public",
        table: table as string,
      };

      // Add filter if provided
      if (config.filter) {
        pgConfig.filter = `${config.filter.column}=${config.filter.operator}.${config.filter.value}`;
      }

      // Use updated Supabase realtime API
      const channel = this.supabase
        .channel(`unified-realtime-${channelKey}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: pgConfig.schema,
            table: pgConfig.table,
            filter: pgConfig.filter,
          },
          (payload) => {
            this.handleRealtimeUpdate(
              table,
              payload as RealtimePostgresChangesPayload<
                Record<string, unknown>
              >,
              config
            );
          }
        )
        .subscribe((status) => {
          logger.info(
            `📡 Unified realtime subscription status for ${table}:`, { status }
          );

          if (status === "SUBSCRIBED") {
            this.reconnectAttempts.set(channelKey, 0);
            this.updateMetrics({ connectionsActive: this.channels.size });
          } else if (status === "CHANNEL_ERROR") {
            this.handleChannelError(table, channelKey, config);
          }
        });

      this.channels.set(channelKey, channel);
    } catch (error) {
      logger.error(`❌ Failed to create channel for ${table}:`, { error });
      this.handleChannelError(table, channelKey, config);
      throw error;
    }
  }

  /**
   * Handle real-time updates with deduplication and performance monitoring
   */
  private handleRealtimeUpdate(
    table: keyof RealtimeTableMap,
    payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
    config: SubscriptionConfig
  ): void {
    const startTime = performance.now();

    try {
      // Extract record ID for deduplication - safely access the id property
      const newRecord = payload.new as Record<string, unknown> | undefined;
      const oldRecord = payload.old as Record<string, unknown> | undefined;
      const recordId = (newRecord?.id ?? oldRecord?.id) as string | undefined;
      const timestamp = Date.now();

      // Apply deduplication if enabled
      if (this.config.enableDeduplication && recordId) {
        const shouldProcess = this.deduplicator.shouldProcess(
          table,
          payload.eventType,
          recordId,
          timestamp,
          config.deduplicationWindow
        );

        if (!shouldProcess) {
          logger.info(
            `🔄 Duplicate message blocked for ${table}:${payload.eventType}:${recordId}`
          );
          return;
        }
      }

      // Create typed payload
      const typedPayload: RealtimePayload = {
        eventType: payload.eventType as "INSERT" | "UPDATE" | "DELETE",
        new: payload.new,
        old: payload.old,
        schema: payload.schema,
        table: payload.table,
        commit_timestamp: payload.commit_timestamp,
      };

      logger.info(
        `🔔 Unified realtime update for ${table}:`, { eventType: payload.eventType,
        recordId
      });

      // Notify all subscribers for this table
      const channelKey = this.generateChannelKey(table, config.filter);
      const subscribers = this.subscribers.get(channelKey);

      if (subscribers) {
        const promises: Promise<void>[] = [];

        subscribers.forEach((callback) => {
          try {
            const result = callback(typedPayload);
            if (result instanceof Promise) {
              promises.push(result);
            }
          } catch (error) {
            logger.error("❌ Error in realtime callback:", { error });
            this.updateMetrics({ errorRate: this.calculateErrorRate() });
          }
        });

        // Wait for all async callbacks if any
        if (promises.length > 0) {
          Promise.allSettled(promises).then((results) => {
            const errors = results.filter((r) => r.status === "rejected");
            if (errors.length > 0) {
              logger.error(
                `❌ ${errors.length} async callback errors for ${table}`
              );
              this.updateMetrics({ errorRate: this.calculateErrorRate() });
            }
          });
        }
      }

      // Update performance metrics
      const processingTime = performance.now() - startTime;
      this.updateMetrics({
        messagesReceived: this.metrics.messagesReceived + 1,
        messagesProcessed: this.metrics.messagesProcessed + 1,
        averageLatency: this.calculateAverageLatency(processingTime),
        lastActivity: new Date(),
      });
    } catch (error) {
      logger.error(`❌ Error handling realtime update for ${table}:`, { error });
      this.updateMetrics({
        errorRate: this.calculateErrorRate(),
        messagesReceived: this.metrics.messagesReceived + 1,
      });
    }
  }

  /**
   * Handle channel errors with exponential backoff reconnection
   */
  private handleChannelError(
    table: keyof RealtimeTableMap,
    channelKey: string,
    config: SubscriptionConfig
  ): void {
    const attempts = this.reconnectAttempts.get(channelKey) ?? 0;

    if (attempts < this.config.maxReconnectAttempts) {
      const backoffDelay = Math.min(
        this.config.reconnectInterval * Math.pow(2, attempts),
        30000 // Max 30 seconds
      );

      logger.info(
        `🔄 Attempting to reconnect ${table} (attempt ${attempts + 1}) in ${backoffDelay}ms`
      );

      setTimeout(async () => {
        try {
          this.removeChannel(channelKey);
          await this.createChannel(table, config, channelKey);
          this.reconnectAttempts.set(channelKey, attempts + 1);
        } catch (error) {
          logger.error(`❌ Reconnection failed for ${table}:`, { error });
          this.handleChannelError(table, channelKey, config);
        }
      }, backoffDelay);
    } else {
      logger.error(
        `❌ Max reconnection attempts reached for ${table} (${attempts})`
      );
      this.updateMetrics({ errorRate: this.calculateErrorRate() });
    }
  }

  /**
   * Remove a channel and clean up its subscribers
   */
  private removeChannel(channelKey: string): void {
    const channel = this.channels.get(channelKey);
    if (channel) {
      this.supabase.removeChannel(channel);
      this.channels.delete(channelKey);
    }
  }

  /**
   * Unsubscribe a specific callback
   */
  private unsubscribeCallback(
    channelKey: string,
    callback: RealtimeCallback
  ): void {
    this.subscribers.get(channelKey)?.delete(callback);

    // Clean up channel if no more subscribers
    if (this.subscribers.get(channelKey)?.size === 0) {
      this.removeChannel(channelKey);
      this.subscribers.delete(channelKey);
    }

    this.updateMetrics({ subscriptionsTotal: this.getTotalSubscriptions() });
  }

  /**
   * Generate a unique channel key based on table and filter
   */
  private generateChannelKey(
    table: keyof RealtimeTableMap,
    filter?: RealtimeFilter
  ): string {
    if (!filter) {
      return table;
    }
    return `${table}:${filter.column}:${filter.operator}:${filter.value}`;
  }

  /**
   * Get total number of active subscriptions
   */
  private getTotalSubscriptions(): number {
    return Array.from(this.subscribers.values()).reduce(
      (total, set) => total + set.size,
      0
    );
  }

  /**
   * Initialize performance metrics
   */
  private initializeMetrics(): RealtimeMetrics {
    return {
      connectionsActive: 0,
      subscriptionsTotal: 0,
      messagesReceived: 0,
      messagesProcessed: 0,
      reconnectionAttempts: 0,
      averageLatency: 0,
      errorRate: 0,
      lastActivity: new Date(),
      uptime: 0,
    };
  }

  /**
   * Update performance metrics
   */
  private updateMetrics(updates: Partial<RealtimeMetrics>): void {
    if (!this.config.enableMetrics) {return;}

    this.metrics = { ...this.metrics, ...updates };
  }

  /**
   * Calculate average latency with exponential moving average
   */
  private calculateAverageLatency(newLatency: number): number {
    const alpha = 0.1; // Smoothing factor
    return this.metrics.averageLatency * (1 - alpha) + newLatency * alpha;
  }

  /**
   * Calculate error rate based on recent activity
   */
  private calculateErrorRate(): number {
    if (this.metrics.messagesReceived === 0) {return 0;}
    const errors =
      this.metrics.messagesReceived - this.metrics.messagesProcessed;
    return errors / this.metrics.messagesReceived;
  }

  /**
   * Start performance monitoring loop
   */
  private startPerformanceMonitoring(): void {
    if (!this.config.enablePerformanceMonitoring) {return;}

    setInterval(() => {
      const health = this.getConnectionHealth();
      if (!health.data?.healthy) {
        logger.warn(
          "⚠️ Unified Real-time Service health issues detected:", { issues: health.data?.issues }
        );
      }

      // Log metrics periodically (every 5 minutes)
      if (Date.now() % (5 * 60 * 1000) < 1000) {
        const metrics = this.getMetrics();
        logger.info("📊 Unified Real-time Service metrics:", { metrics: metrics.data });
      }
    }, 60000); // Check every minute
  }
}

// Removed all legacy exports:
// export const unifiedRealtimeService = new UnifiedRealtimeService({
//   enableSync: true,
//   enableMetrics: true,
//   enableDeduplication: true,
//   enablePerformanceMonitoring: true,
//   maxChannels: 50,
//   globalDebounceMs: 100,
// });
// export const realtimeSyncService = unifiedRealtimeService;
// export type RealtimeSyncService = UnifiedRealtimeService;
